import { Grid, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Configuration,
  CONFIGURATION_TYPE,
  getWorkOrdersWithFilters,
  MIXPANEL_EVENTS,
  PermissionsEnum,
  Property,
  WORK_ORDER_CREATION_STATUS,
  WorkOrder,
} from 'vendor-marketplace-shared-library';
import WorkOrderCard from '../../../components/work-orders/WorkOrderCard';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import appConfig from '../../../config/app.config';
import { useQuery } from '@apollo/client';
import useAuth from '../../../hooks/useAuth';
import TenantPortalService from '../../../services/tenant-portal.service';
import useAlert from '../../../hooks/useAlert';
import { WEB_LINK_CREATE } from '../../../error-messages';
import useMixpanel from '../../../hooks/useMixpanel';
import { getConfigurationValue } from '../../../utilities/helpers';
import useConfigurations from '../../../hooks/useConfigurations';

export default function WorkOrderContainerModule(props: {
  property: Property;
  refetchProperty: () => void;
}) {
  const { setAlert } = useAlert();
  const { validateUserPermissions } = useAuth();
  const navigate = useNavigate();
  const { configurationsQuery } = useConfigurations();
  const { mixpanelTrack } = useMixpanel();

  const { data: workOrderData } = useQuery(getWorkOrdersWithFilters, {
    variables: {
      propertyIds: [Number(props.property.id)],
      creationStatus: WORK_ORDER_CREATION_STATUS.COMPLETE,
    },
  });

  const generateTenantWebFormUrl = (hash: string) => {
    return `${appConfig.appUrl}/tenant/work-order-form/${hash}`;
  };

  const navigateToForm = () => {
    if (props.property.tenantWorkOrderForm?.hash) {
      const url = generateTenantWebFormUrl(props.property.tenantWorkOrderForm.hash || '');
      window.open(url, '_blank');
    }
  };

  const onEnableTenantPortalToggle = async (enabled: boolean) => {
    try {
      await TenantPortalService.updateTenantWorkOrderForm(props.property.id, enabled);
      props.refetchProperty();
    } catch (err: any) {
      setAlert({
        message: err?.message ?? WEB_LINK_CREATE,
      });
    }
  };

  const userWarrantyProductIsEnabledConfiguration =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.WARRANTY_PRODUCT_IS_ENABLED,
    );

  const isUserWarrantyProductEnabled = getConfigurationValue(
    userWarrantyProductIsEnabledConfiguration,
  );

  return (
    <Grid item container direction={'column'} sx={styles.workOrderContainer} boxShadow={2}>
      {/*Title*/}
      <Grid item container sx={styles.centeredApart}>
        <Grid item>
          <Typography variant={'h6'}>Work Orders</Typography>
        </Grid>
        {validateUserPermissions([PermissionsEnum.WORK_ORDERS_CREATE]) && (
          <Grid item>
            <IconButton
              onClick={() => navigate(`/create-work-order?propertyId=${props.property.id}`)}
            >
              <AddIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>

      {/*Tenant web form toggle*/}
      {!isUserWarrantyProductEnabled && (
        <Grid item container sx={styles.centeredApart}>
          <Grid container sx={styles.centeredContainer}>
            <Grid item>
              <Typography>Tenant web form link</Typography>
            </Grid>
            {props.property.tenantWorkOrderForm?.enabled && (
              <Grid item>
                <IconButton onClick={navigateToForm}>
                  <OpenInNewIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
          <Grid item>
            <Tooltip
              title={
                'Enabling will allow your tenants to submit work orders via a web form. Copy and share this link with your tenants'
              }
            >
              <Switch
                checked={props.property.tenantWorkOrderForm?.enabled ?? false}
                onClick={() =>
                  onEnableTenantPortalToggle(!props.property.tenantWorkOrderForm?.enabled)
                }
              />
            </Tooltip>
          </Grid>
        </Grid>
      )}

      {/*Work Orders*/}
      <Grid container sx={styles.workOrderCardContainer}>
        {workOrderData?.getWorkOrdersWithFilters.length > 0 ? (
          workOrderData?.getWorkOrdersWithFilters.map((workOrder: WorkOrder) => (
            <Grid item key={workOrder.id}>
              <WorkOrderCard
                workOrder={workOrder}
                onClick={() => {
                  navigate(`/work-orders/${workOrder.id}`);
                  mixpanelTrack(
                    MIXPANEL_EVENTS.ORGANIZATION_SETTINGS_PROPERTY_DRAWER_CREATE_WORK_ORDER_PRESSED,
                    {
                      propertyId: props.property.id,
                    },
                  );
                }}
              />
            </Grid>
          ))
        ) : (
          <Grid item>
            <Typography>No work orders for this property yet.</Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

const styles = {
  centeredContainer: {
    alignItems: 'center',
  },
  centeredApart: {
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
  },
  workOrderContainer: {
    rowGap: 2,
    padding: 1,
    borderRadius: 2,
    backgroundColor: 'secondary.light',
  },
  workOrderCardContainer: {
    rowGap: 2,
    columnGap: 2,
    overflow: 'auto',
    maxHeight: '800px',
  },
};
